import React, { useState, useEffect } from 'react';
import { adminAPI } from './services/api';
import './GeneralSettingsTab.css';

interface GeneralSettings {
  hotel_name: string;
  contact_phone: string;
  contact_email: string;
  address: string;
  tax_rates: {
    kdv_rate: number;
    accommodation_tax_rate: number;
  };
  webchat: {
    enabled: boolean;
    script_url: string;
    channel_id: string;
  };
}

const GeneralSettingsTab: React.FC = () => {
  const [settings, setSettings] = useState<GeneralSettings>({
    hotel_name: '',
    contact_phone: '',
    contact_email: '',
    address: '',
    tax_rates: {
      kdv_rate: 10,
      accommodation_tax_rate: 1
    },
    webchat: {
      enabled: false,
      script_url: 'https://asistomni.com/widget.js',
      channel_id: ''
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      console.log('🔄 Settings yükleniyor...');
      
      const response = await adminAPI.getGeneralSettings();
      console.log('📥 Backend response:', response.data);
      
      if (response.data.success) {
        console.log('✅ Settings yüklendi:', response.data.data);
        const data = response.data.data || {};
        setSettings({
          hotel_name: data.hotel_name || '',
          contact_phone: data.contact_phone || '',
          contact_email: data.contact_email || '',
          address: data.address || '',
          tax_rates: {
            kdv_rate: Number.isFinite(Number(data?.tax_rates?.kdv_rate))
              ? Number(data.tax_rates.kdv_rate)
              : 10,
            accommodation_tax_rate: Number.isFinite(Number(data?.tax_rates?.accommodation_tax_rate))
              ? Number(data.tax_rates.accommodation_tax_rate)
              : 1
          },
          webchat: {
            enabled: !!data?.webchat?.enabled,
            script_url: data?.webchat?.script_url || 'https://asistomni.com/widget.js',
            channel_id: data?.webchat?.channel_id || ''
          }
        });
      }
    } catch (error: any) {
      console.error('❌ Settings yükleme hatası:', error);
      showMessage('error', 'Ayarlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validasyon
    if (!settings.hotel_name.trim()) {
      showMessage('error', 'Otel adı boş bırakılamaz');
      return;
    }

    if (!settings.contact_email.trim()) {
      showMessage('error', 'Email adresi boş bırakılamaz');
      return;
    }

    // Email formatı kontrolü
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(settings.contact_email)) {
      showMessage('error', 'Geçerli bir email adresi girin');
      return;
    }

    if (!settings.contact_phone.trim()) {
      showMessage('error', 'Telefon numarası boş bırakılamaz');
      return;
    }

    if (!Number.isFinite(settings.tax_rates.kdv_rate) || settings.tax_rates.kdv_rate < 0 || settings.tax_rates.kdv_rate > 20) {
      showMessage('error', 'KDV oranı 0-20 arasında olmalıdır');
      return;
    }

    if (!Number.isFinite(settings.tax_rates.accommodation_tax_rate) || settings.tax_rates.accommodation_tax_rate < 0 || settings.tax_rates.accommodation_tax_rate > 5) {
      showMessage('error', 'Konaklama vergisi oranı 0-5 arasında olmalıdır');
      return;
    }

    if (settings.webchat.enabled) {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!settings.webchat.channel_id.trim() || !uuidRegex.test(settings.webchat.channel_id.trim())) {
        showMessage('error', 'Webchat aktifken geçerli bir Channel ID girin');
        return;
      }
    }

    try {
      setSaving(true);
      console.log('💾 Kaydediliyor:', settings);
      
      const response = await adminAPI.updateGeneralSettings(settings);
      console.log('📥 Backend response:', response.data);
      
      if (response.data.success) {
        showMessage('success', 'Ayarlar başarıyla kaydedildi');
        // Backend'den güncel değerleri yeniden yükle
        await loadSettings();
      } else {
        showMessage('error', response.data.message || 'Kaydetme başarısız');
      }
    } catch (error: any) {
      console.error('❌ Settings kaydetme hatası:', error);
      showMessage('error', error.response?.data?.message || 'Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  if (loading) {
    return (
      <div className="general-settings-tab">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Ayarlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="general-settings-tab">
      <div className="settings-header">
        <h2>🏨 Genel Ayarlar</h2>
        <p>Otel bilgilerinizi ve iletişim detaylarınızı güncelleyin. Bu bilgiler email bildirimlerinde kullanılır.</p>
      </div>

      {message && (
        <div className={`message-box ${message.type}`}>
          <span className="message-icon">{message.type === 'success' ? '✅' : '❌'}</span>
          <span className="message-text">{message.text}</span>
          <button className="message-close" onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="settings-form">
        <div className="form-section">
          <h3>🏨 Otel Bilgileri</h3>
          
          <div className="form-group">
            <label htmlFor="hotel_name">
              Otel Adı <span className="required">*</span>
            </label>
            <input
              type="text"
              id="hotel_name"
              value={settings.hotel_name}
              onChange={(e) => setSettings({ ...settings, hotel_name: e.target.value })}
              placeholder="Örn: Villa Polikne"
              className="form-input"
            />
            <small className="form-help">Email'lerde ve rezervasyon onaylarında görünecek</small>
          </div>

          <div className="form-group">
            <label htmlFor="address">
              Adres
            </label>
            <textarea
              id="address"
              value={settings.address}
              onChange={(e) => setSettings({ ...settings, address: e.target.value })}
              placeholder="Örn: Polikne Koyu, Fethiye/Muğla, Türkiye"
              className="form-textarea"
              rows={3}
            />
            <small className="form-help">Rezervasyon email'lerinde gösterilir</small>
          </div>
        </div>

        <div className="form-section">
          <h3>📞 İletişim Bilgileri</h3>
          
          <div className="form-group">
            <label htmlFor="contact_phone">
              Telefon Numarası <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="contact_phone"
              value={settings.contact_phone}
              onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
              placeholder="Örn: +90 252 XXX XX XX"
              className="form-input"
            />
            <small className="form-help">Email'lerde ve rezervasyon bilgilerinde görünecek</small>
          </div>

          <div className="form-group">
            <label htmlFor="contact_email">
              Email Adresi <span className="required">*</span>
            </label>
            <input
              type="email"
              id="contact_email"
              value={settings.contact_email}
              onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
              placeholder="Örn: info@villapolikne.com"
              className="form-input"
            />
            <small className="form-help">Müşterilerin iletişim kurabileceği email adresi</small>
          </div>
        </div>

        <div className="form-section">
          <h3>🧾 Vergi Oranları</h3>

          <div className="form-group">
            <label htmlFor="kdv_rate">KDV Oranı (%)</label>
            <input
              type="number"
              id="kdv_rate"
              min={0}
              max={20}
              step="0.01"
              value={settings.tax_rates.kdv_rate}
              onChange={(e) => setSettings({
                ...settings,
                tax_rates: {
                  ...settings.tax_rates,
                  kdv_rate: Number(e.target.value)
                }
              })}
              className="form-input"
            />
            <small className="form-help">Paraşüt fatura kalemlerindeki KDV oranı</small>
          </div>

          <div className="form-group">
            <label htmlFor="accommodation_tax_rate">Konaklama Vergisi Oranı (%)</label>
            <input
              type="number"
              id="accommodation_tax_rate"
              min={0}
              max={5}
              step="0.01"
              value={settings.tax_rates.accommodation_tax_rate}
              onChange={(e) => setSettings({
                ...settings,
                tax_rates: {
                  ...settings.tax_rates,
                  accommodation_tax_rate: Number(e.target.value)
                }
              })}
              className="form-input"
            />
            <small className="form-help">Konaklama kalemi için ek vergi oranı</small>
          </div>
        </div>

        <div className="form-section">
          <h3>💬 Webchat Widget</h3>

          <div className="form-group">
            <label htmlFor="webchat_enabled">Webchat Durumu</label>
            <div className="checkbox-row">
              <input
                type="checkbox"
                id="webchat_enabled"
                checked={settings.webchat.enabled}
                onChange={(e) => setSettings({
                  ...settings,
                  webchat: {
                    ...settings.webchat,
                    enabled: e.target.checked
                  }
                })}
              />
              <span>{settings.webchat.enabled ? 'Aktif' : 'Pasif'}</span>
            </div>
            <small className="form-help">Aktif edildiğinde siteye AsistOmni webchat widget scripti yüklenir</small>
          </div>

          <div className="form-group">
            <label htmlFor="webchat_channel_id">
              Channel ID {settings.webchat.enabled && <span className="required">*</span>}
            </label>
            <input
              type="text"
              id="webchat_channel_id"
              value={settings.webchat.channel_id}
              onChange={(e) => setSettings({
                ...settings,
                webchat: {
                  ...settings.webchat,
                  channel_id: e.target.value
                }
              })}
              placeholder="Örn: 44625961-ec35-400a-bdbd-42b7ad8a8640"
              className="form-input"
            />
            <small className="form-help">AsistOmni kanal UUID değeri</small>
          </div>

          <div className="form-group">
            <label htmlFor="webchat_script_url">Script URL</label>
            <input
              type="text"
              id="webchat_script_url"
              value={settings.webchat.script_url}
              onChange={(e) => setSettings({
                ...settings,
                webchat: {
                  ...settings.webchat,
                  script_url: e.target.value
                }
              })}
              placeholder="https://asistomni.com/widget.js"
              className="form-input"
            />
            <small className="form-help">Güvenlik nedeniyle sadece asistomni.com alan adına izin verilir</small>
          </div>
        </div>

        <div className="form-actions">
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-small"></span>
                Kaydediliyor...
              </>
            ) : (
              <>
                💾 Kaydet
              </>
            )}
          </button>
          
          <button
            className="btn-cancel"
            onClick={loadSettings}
            disabled={saving}
          >
            🔄 Sıfırla
          </button>
        </div>
      </div>

      <div className="settings-info">
        <div className="info-card">
          <h4>ℹ️ Bilgi</h4>
          <ul>
            <li>Bu bilgiler tüm otomatik email bildirimlerinde kullanılır</li>
            <li>Rezervasyon onay email'lerinde footer'da görünür</li>
            <li>Müşteriler bu iletişim bilgilerini kullanarak size ulaşabilir</li>
            <li>Değişiklikler hemen aktif olur, sunucuyu yeniden başlatmanıza gerek yoktur</li>
            <li>Webchat tek sayfa uygulamada sayfalar arası açık kalır; tam sayfa yenilemede widget kendi oturum mantığına göre devam eder</li>
          </ul>
        </div>

        <div className="info-card preview">
          <h4>👁️ Email Önizleme</h4>
          <div className="email-preview">
            <div className="email-header">
              <h3>🎉 Rezervasyonunuz Onaylandı!</h3>
              <p>{settings.hotel_name || 'Otel Adı'}</p>
            </div>
            <div className="email-footer">
              <p>📞 {settings.contact_phone || 'Telefon'} | ✉️ {settings.contact_email || 'Email'}</p>
              <p>© {new Date().getFullYear()} {settings.hotel_name || 'Otel Adı'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralSettingsTab;
